import { ChangeDetectorRef, Component, EventEmitter, Injector, Input, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { IOption } from '@app/main/master-filter/cascade-selection.component';
import MasterFilterService2 from '@app/main/master-filter/master-filter.service2';
import ButtonType from '@app/shared/button/button-type';
import AppComponentBase from '@shared/common/app-component-base';
import { ILabelOption } from '@shared/common/common-types';
import { getYesNoOptions } from '@shared/common/yes-no-options';
import { isNil } from 'lodash';

@Component({
    selector: 'app-advanced-filters',
    templateUrl: './advanced-filters.component.html',
    styleUrls: ['./advanced-filters.component.less'],
    standalone: false,
})
class AdvancedFiltersComponent extends AppComponentBase implements OnInit {

    @Input() ShowApartmentFilters: boolean;

    @Input() ShowSearchesFilters: boolean;

    @Input() ShowHouseFilters: boolean;

    @Input() isIndividual: boolean = false;

    @Output() trueValuesChange = new EventEmitter<{ [key: string]: string }>();

    @Output() allValuesCleared = new EventEmitter<number>();

    filtersForm: FormGroup;

    resetFlag: boolean = false;

    selectedSecondLevelOptions: IOption[];

    filtersOptionsOpenState = {
        isComplexVisible: false,
        isPoolVisible: false,
        isFloorsVisible: false,
        isDepositVisible: false,
        isBedroomsVisible: false,
        isBathroomsVisible: false,
        isPremisesVisible: false,
    };

    stateOptions: ILabelOption[] = getYesNoOptions((k) => this.l(k));

    loadedCategoryResultsCounts: { [key: string]: number } = {
        characteristics: 0,
        advantages: 0,
        estates: 0,
        details: 0,
        advertisement: 0,
        searches: 0,
    };

    categoryFilters = {
        searches: ['externalAgency', 'Deposit', 'InterestInProjects', 'InterestInOffers', 'Furnishing', 'Bedrooms', 'Bathrooms'],
        characteristics: ['Floor', 'Elevator', 'ExternalBroker', 'Heating', 'Parking', 'Condition', 'Exposure'],
        advantages: ['GatedComplex', 'Yard', 'Fitness', 'View', 'Pool', 'Security'],
        estates: ['Project', 'Construction Type', 'Stage of Completion', 'Year of Construction', 'Number of Floors'],
        details: ['Publication Date', 'Agency', 'Consultant', 'Visible on Site', 'Published', 'Transferred from...', 'Source'],
        advertisement: ['Offer Status', 'Documents', 'Reference', 'Confidential', 'Key', 'Inspections', 'Photos'],
    };

    protected readonly ButtonType = ButtonType;


    constructor(
        injector: Injector,
        private cdr: ChangeDetectorRef,
        private masterFilterService: MasterFilterService2,
    ) {
        super(injector);
    }

    ngOnInit(): void {
        this.masterFilterService
            .getSelectedEstateCategorySecondLevel()
            .subscribe((result) => {
                this.selectedSecondLevelOptions = result;
            });

        this.filtersForm = new FormGroup(this.getDefaultFormState());
        this.updateLoadedCategoryResultsCounts();
    }

    clearValues() {
        this.resetFormControls();
        this.allValuesCleared.emit();
        this.updateLoadedCategoryResultsCounts();
        this.resetFlag = !this.resetFlag;

        if (this.filtersOptionsOpenState.isComplexVisible) {
            this.filtersOptionsOpenState.isComplexVisible = false;
        }
        if (this.filtersOptionsOpenState.isPoolVisible) {
            this.filtersOptionsOpenState.isPoolVisible = false;
        }

    }

    onCheck() {
        const { value, poolValue, ...remainingValues } = this.filtersForm.value;
        const trueValues = Object.keys(remainingValues)
            .filter(key => !isNil(remainingValues[key]) && remainingValues[key] !== false)
            .reduce((obj, key) => {
                obj[key] = remainingValues[key];
                return obj;
            }, {});

        this.trueValuesChange.emit(trueValues);

        this.updateLoadedCategoryResultsCounts();
    }


    //TODO: include only the poolValue value and GatedComplex value in the object
    //TODO: more generic, not so specific
    setCombinedValues() {
        const formValues = this.filtersForm.value;
        const combinedValues: { [key: string]: boolean } = {};

        // Handle 'GatedComplex' and its associated 'value'
        if (this.filtersForm.controls.GatedComplex.value) {
            combinedValues.GatedComplex = true;
            combinedValues.value = formValues.value; // Include 'value' based on GatedComplex
        }

        // Handle 'Pool' and its associated 'poolValue'
        if (this.filtersForm.controls.Pool.value) {
            combinedValues.Pool = true;
            combinedValues.poolValue = formValues.poolValue; // Include 'poolValue' based on Pool
        }

        // Add all other true values normally, skipping 'value' and
        // 'poolValue' as they are handled above
        for (const key in formValues) {
            if (formValues.hasOwnProperty(key) && key !== 'value' && key !== 'poolValue' && formValues[key] === true) {
                combinedValues[key] = formValues[key];
            }
        }

    }


    toggleComplexFilterOption() {
        this.filtersOptionsOpenState.isComplexVisible = !this.filtersOptionsOpenState.isComplexVisible;
    }

    togglePoolFilterOption() {
        this.filtersOptionsOpenState.isPoolVisible = !this.filtersOptionsOpenState.isPoolVisible;
    }

    toggleFloorsFilterOption() {
        this.filtersOptionsOpenState.isFloorsVisible = !this.filtersOptionsOpenState.isFloorsVisible;
    }

    toggleDepositFilterOption() {
        this.filtersOptionsOpenState.isDepositVisible = !this.filtersOptionsOpenState.isDepositVisible;
    }

    toggleBedroomsFilterOption() {
        this.filtersOptionsOpenState.isBedroomsVisible = !this.filtersOptionsOpenState.isBedroomsVisible;
    }

    toggleBathroomsFilterOption() {
        this.filtersOptionsOpenState.isBathroomsVisible = !this.filtersOptionsOpenState.isBathroomsVisible;
    }

    togglePremisesFilterOption() {
        this.filtersOptionsOpenState.isPremisesVisible = !this.filtersOptionsOpenState.isPremisesVisible;
    }

    isEstateCategorySelected(estateCategoryId): boolean {
        return this.selectedSecondLevelOptions?.some(option => option.id === estateCategoryId);
    }

    showHouseAdvancedFilter(): boolean {
        return this.masterFilterService.isPropertySelected('Етаж от къща');
    }

    private getDefaultFormState() {
        return {
            Floor: new FormControl(false),
            Elevator: new FormControl(false),
            ExternalBroker: new FormControl(false),
            Heating: new FormControl(false),
            Parking: new FormControl(false),
            Condition: new FormControl(false),
            Exposure: new FormControl(false),
            GatedComplex: new FormControl(false),
            value: new FormControl(true),
            poolValue: new FormControl(true),
            Yard: new FormControl(false),
            Fitness: new FormControl(false),
            View: new FormControl(false),
            Pool: new FormControl(false),
            Security: new FormControl(false),
            Project: new FormControl(false),
            'Construction Type': new FormControl(false),
            'Stage of Completion': new FormControl(false),
            'Year of Construction': new FormControl(false),
            'Number of Floors': new FormControl(false),
            'Publication Date': new FormControl(false),
            Agency: new FormControl(false),
            Consultant: new FormControl(false),
            'Visible on Site': new FormControl(false),
            Published: new FormControl(false),
            'Transferred from': new FormControl(false),
            Source: new FormControl(false),
            'Offer Status': new FormControl(false),
            Documents: new FormControl(false),
            Reference: new FormControl(false),
            Confidential: new FormControl(false),
            Key: new FormControl(false),
            Inspections: new FormControl(false),
            Photos: new FormControl(false),
            Deposit: new FormControl(false),
            depositValue: new FormControl(false),
            ExternalAgency: new FormControl(false),
            InterestInProjects: new FormControl(false),
            InterestInOffers: new FormControl(false),
            Furnishing: new FormControl(false),
            Bedrooms: new FormControl(false),
            Bathrooms: new FormControl(false),
            Premises: new FormControl(false),
            'Number of Bedrooms': new FormControl(false),
            'Number of Bathrooms': new FormControl(false),
            'Number of Premises': new FormControl(false),
        };
    }

    private resetFormControls() {
        const defaultState = this.getDefaultFormState();
        Object.keys(defaultState)
            .forEach(key => {
                if (this.filtersForm.controls[key]) {
                    this.filtersForm.controls[key].setValue(defaultState[key].value);
                }
            });
    }

    private updateLoadedCategoryResultsCounts() {
        const { GatedComplex, ...restFilters } = this.filtersForm.value;

        for (const category in this.categoryFilters) {
            if (this.categoryFilters.hasOwnProperty(category)) {
                this.loadedCategoryResultsCounts[category] = this.categoryFilters[category]
                    .filter(key => {
                        if (key === 'GatedComplex') {
                            return GatedComplex === true;
                        }
                        return !isNil(restFilters[key]) && restFilters[key] !== false;
                    }).length;
            }
        }

        this.cdr.detectChanges();
    }
}
export default AdvancedFiltersComponent;
